import {
  billingLifecycleCustomerCopy,
  billingLifecycleHeadline,
  isCompedEntitlement,
  LSC_BILLING_OWNER_MANAGED_COPY,
  LSC_BILLING_UI_DISABLED_MESSAGE,
  LSC_PAID_UNBOUND_RECOVERY_MESSAGE,
  LSC_PRICING_HERO_COPY,
  LSC_PRICING_PLAN_NOTES,
  LSC_SUBSCRIPTION_VALUE_THEMES,
  LSC_WIZARD_SUBSCRIPTION_INTRO_COPY,
  LSC_WIZARD_SUBSCRIPTION_MANAGE_NOTE,
} from './billingCopy';
import { isActivePaidAccountStatus } from './entitlementBillingErrors';
import type { LscBillingStatusResponse } from './types';

export type BillingPresentationSurface =
  | 'public_pricing'
  | 'account_pricing'
  | 'setup_subscription'
  | 'league_billing_settings'
  | 'account_billing';

export type BillingPresentationMode =
  | 'anonymous'
  | 'ui_disabled'
  | 'owner_managed'
  | 'member_read_only'
  | 'eligible_owner'
  | 'subscribed_bound'
  | 'paid_unbound'
  | 'comped'
  | 'checkout_disabled'
  | 'past_due'
  | 'canceled'
  | 'unknown';

export type BillingPresentationCtaAction =
  | 'checkout'
  | 'bind'
  | 'portal'
  | 'sign_in'
  | 'billing'
  | 'setup'
  | 'pricing';

export type BillingPresentationCta = {
  code: string;
  label: string;
  href?: string;
  action?: BillingPresentationCtaAction;
};

export type LscBillingPresentation = {
  mode: BillingPresentationMode;
  headline: string;
  valueProposition: string;
  detailLines: string[];
  planNotes: readonly string[];
  primaryCta: BillingPresentationCta | null;
  secondaryCtas: BillingPresentationCta[];
  showCheckoutActions: boolean;
  showPricingCards: boolean;
  showBindCta: boolean;
  showManageBilling: boolean;
  showStatusCard: boolean;
  showReturnToSetup: boolean;
  disabledBanner: 'none' | 'ui_disabled' | 'lifecycle';
  disabledReason?: string;
  reasonCodes: string[];
};

export type ResolveLscBillingPresentationInput = {
  status: LscBillingStatusResponse | null;
  surface: BillingPresentationSurface;
  viewer: { isAuthenticated: boolean; isOwner?: boolean; isOperator?: boolean };
  context: {
    billingUiEnabled: boolean;
    leagueId?: string;
    leagueArchived?: boolean;
  };
};

type CoreModeResult = {
  mode: BillingPresentationMode;
  reasonCodes: string[];
  lifecycleMessage?: string;
  lifecycleHeadline?: string | null;
};

function isLeagueSurface(surface: BillingPresentationSurface): boolean {
  return surface === 'setup_subscription' || surface === 'league_billing_settings';
}

function lifecycleDisabledMessage(status: LscBillingStatusResponse | null): string | undefined {
  if (!status?.billing_lifecycle) return undefined;
  return (
    status.billing_lifecycle.customer_message ||
    billingLifecycleCustomerCopy(status.billing_lifecycle.mode)
  );
}

function resolveCoreMode(input: ResolveLscBillingPresentationInput): CoreModeResult {
  const { status, surface, viewer, context } = input;
  const reasonCodes: string[] = [];
  const leagueSurface = isLeagueSurface(surface);

  if (!viewer.isAuthenticated && surface === 'public_pricing') {
    reasonCodes.push('anonymous_public_pricing');
    return { mode: 'anonymous', reasonCodes };
  }

  if (leagueSurface && viewer.isOperator && viewer.isOwner === false) {
    reasonCodes.push('owner_managed_operator');
    return { mode: 'owner_managed', reasonCodes };
  }

  if (!context.billingUiEnabled) {
    reasonCodes.push('billing_ui_disabled');
    return { mode: 'ui_disabled', reasonCodes };
  }

  const entitlement = status?.entitlement ?? null;
  const isComped = isCompedEntitlement(entitlement);
  const hasLeagueEntitlement = Boolean(entitlement);
  const paidAccountActive = isActivePaidAccountStatus(status);
  const lifecycleMode = (status?.billing_lifecycle?.mode || '').toLowerCase();
  const lifecycleMessage = lifecycleDisabledMessage(status);
  const lifecycleHeadline = billingLifecycleHeadline(status?.billing_lifecycle?.mode);

  if (isComped || lifecycleMode === 'billing_comped') {
    reasonCodes.push('comped_access');
    return { mode: 'comped', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  if (hasLeagueEntitlement || lifecycleMode === 'billing_active') {
    if (hasLeagueEntitlement || status?.league_entitlement_bound === true) {
      reasonCodes.push('subscribed_bound');
      return { mode: 'subscribed_bound', reasonCodes, lifecycleMessage, lifecycleHeadline };
    }
  }

  if (
    paidAccountActive ||
    lifecycleMode === 'billing_unbound' ||
    lifecycleMode === 'billing_recovery_available' ||
    status?.subscription_state === 'account_active_unbound'
  ) {
    if (!hasLeagueEntitlement) {
      reasonCodes.push('paid_unbound');
      return { mode: 'paid_unbound', reasonCodes, lifecycleMessage, lifecycleHeadline };
    }
  }

  if (lifecycleMode === 'billing_past_due') {
    reasonCodes.push('lifecycle_past_due');
    return { mode: 'past_due', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  if (lifecycleMode === 'billing_canceled') {
    reasonCodes.push('lifecycle_canceled');
    return { mode: 'canceled', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  const checkoutEligible =
    Boolean(status?.billing_model_enabled) &&
    status?.checkout_enabled === true &&
    status?.can_checkout === true &&
    !context.leagueArchived &&
    (viewer.isOwner !== false || !leagueSurface);

  if (leagueSurface && viewer.isOwner === false) {
    reasonCodes.push('member_read_only');
    return { mode: 'member_read_only', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  if (checkoutEligible) {
    reasonCodes.push('eligible_owner_checkout');
    return { mode: 'eligible_owner', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  if (
    lifecycleMode === 'billing_portal_unavailable' ||
    lifecycleMode === 'billing_enforcement_disabled' ||
    lifecycleMode === 'billing_incomplete' ||
    lifecycleMode === 'billing_unpaid' ||
    lifecycleMode === 'billing_unknown_review_required' ||
    !status?.billing_model_enabled ||
    status?.checkout_enabled === false ||
    status?.can_checkout === false
  ) {
    reasonCodes.push('checkout_disabled');
    return { mode: 'checkout_disabled', reasonCodes, lifecycleMessage, lifecycleHeadline };
  }

  reasonCodes.push('unknown_review');
  return { mode: 'unknown', reasonCodes, lifecycleMessage, lifecycleHeadline };
}

function surfaceValueProposition(surface: BillingPresentationSurface): string {
  switch (surface) {
    case 'setup_subscription':
      return LSC_WIZARD_SUBSCRIPTION_INTRO_COPY;
    case 'league_billing_settings':
      return 'Your league access is activated after checkout and subscription linking completes.';
    case 'account_billing':
      return 'Manage your League Stream Center subscription.';
    case 'account_pricing':
    case 'public_pricing':
    default:
      return LSC_PRICING_HERO_COPY;
  }
}

function buildHeadline(
  core: CoreModeResult,
  surface: BillingPresentationSurface
): string {
  if (core.lifecycleHeadline) {
    return core.lifecycleHeadline;
  }
  if (surface === 'league_billing_settings' && core.mode === 'eligible_owner') {
    return 'League subscription';
  }
  if (surface === 'setup_subscription' && core.mode === 'eligible_owner') {
    return 'League subscription';
  }
  switch (core.mode) {
    case 'anonymous':
      return 'Subscription pricing';
    case 'ui_disabled':
      return 'Subscription billing unavailable';
    case 'owner_managed':
      return 'League subscription';
    case 'member_read_only':
      return 'League subscription';
    case 'eligible_owner':
      return 'Start your league subscription';
    case 'subscribed_bound':
      return 'Subscription active';
    case 'paid_unbound':
      return 'Subscription active — link your league';
    case 'comped':
      return 'Complimentary access active';
    case 'checkout_disabled':
      return 'Checkout temporarily unavailable';
    case 'past_due':
      return 'Subscription — payment attention needed';
    case 'canceled':
      return 'Subscription canceled';
    default:
      return 'Subscription status';
  }
}

function buildDetailLines(core: CoreModeResult, surface: BillingPresentationSurface): string[] {
  const lines: string[] = [];
  if (core.lifecycleMessage) {
    lines.push(core.lifecycleMessage);
  }
  switch (core.mode) {
    case 'anonymous':
      lines.push('Sign in with Twitch to subscribe as a commissioner. Members can join a league for free with an invite code.');
      break;
    case 'owner_managed':
      lines.push(LSC_BILLING_OWNER_MANAGED_COPY);
      lines.push('Ask your League Owner to activate or link the subscription for this league.');
      break;
    case 'paid_unbound':
      if (!core.lifecycleMessage) {
        if (surface === 'league_billing_settings' || surface === 'setup_subscription') {
          lines.push(
            'Your subscription is active, but it is not linked to this league yet.'
          );
        } else {
          lines.push(LSC_PAID_UNBOUND_RECOVERY_MESSAGE);
        }
      }
      if (surface === 'setup_subscription') {
        lines.push('Bind your subscription here to continue setup.');
      }
      break;
    case 'member_read_only':
      lines.push('Billing is managed by the League Owner.');
      lines.push('Ask your commissioner to activate or link the subscription for this league.');
      break;
    case 'comped':
      if (!core.lifecycleMessage) {
        lines.push('Complimentary access is active. No checkout is required.');
      }
      break;
    case 'ui_disabled':
      lines.push(LSC_BILLING_UI_DISABLED_MESSAGE);
      break;
    case 'checkout_disabled':
      break;
    case 'subscribed_bound':
      if (!core.lifecycleMessage) {
        lines.push('Your subscription is linked and active.');
      }
      break;
    default:
      break;
  }
  if (surface === 'setup_subscription' && core.mode === 'eligible_owner') {
    lines.push(LSC_WIZARD_SUBSCRIPTION_MANAGE_NOTE);
  }
  return lines;
}

function buildCtas(
  core: CoreModeResult,
  input: ResolveLscBillingPresentationInput
): { primary: BillingPresentationCta | null; secondary: BillingPresentationCta[] } {
  const { context } = input;
  const leagueId = context.leagueId;
  const setupHref = leagueId ? `/leagues/${leagueId}/setup?step=subscription` : undefined;
  const settingsHref = leagueId ? `/leagues/${leagueId}/settings` : undefined;

  switch (core.mode) {
    case 'anonymous':
      return {
        primary: { code: 'sign_in', label: 'Sign in to subscribe', action: 'sign_in' },
        secondary: [],
      };
    case 'eligible_owner':
      return {
        primary: { code: 'checkout', label: 'Subscribe', action: 'checkout' },
        secondary: [{ code: 'billing', label: 'View billing status', href: '/billing', action: 'billing' }],
      };
    case 'subscribed_bound':
      return {
        primary: {
          code: 'manage_billing',
          label: 'Manage billing',
          href: '/billing',
          action: 'billing',
        },
        secondary: setupHref
          ? [{ code: 'continue_setup', label: 'Continue league setup', href: setupHref, action: 'setup' }]
          : settingsHref
            ? [{ code: 'league_settings', label: 'League subscription settings', href: settingsHref, action: 'billing' }]
            : [],
      };
    case 'paid_unbound':
      return {
        primary: leagueId
          ? { code: 'bind', label: 'Bind subscription to this league', action: 'bind' }
          : {
              code: 'billing',
              label: 'Link subscription in billing',
              href: '/billing',
              action: 'billing',
            },
        secondary: settingsHref
          ? [{ code: 'league_settings', label: 'Open league settings', href: settingsHref, action: 'billing' }]
          : [{ code: 'pricing', label: 'View pricing', href: '/pricing', action: 'pricing' }],
      };
    case 'past_due':
    case 'canceled':
      return {
        primary: { code: 'manage_billing', label: 'Manage billing', href: '/billing', action: 'billing' },
        secondary: [{ code: 'pricing', label: 'View pricing', href: '/pricing', action: 'pricing' }],
      };
    case 'checkout_disabled':
    case 'unknown':
      return {
        primary: { code: 'billing', label: 'View billing status', href: '/billing', action: 'billing' },
        secondary: [{ code: 'pricing', label: 'View pricing', href: '/pricing', action: 'pricing' }],
      };
    default:
      return { primary: null, secondary: [] };
  }
}

/** Resolve customer-safe billing/pricing presentation for a surface. */
export function resolveLscBillingPresentation(
  input: ResolveLscBillingPresentationInput
): LscBillingPresentation {
  const core = resolveCoreMode(input);
  const { surface, context } = input;
  const ctas = buildCtas(core, input);

  const showCheckoutActions = core.mode === 'eligible_owner' && context.billingUiEnabled;
  const showPricingCards =
    core.mode === 'eligible_owner' ||
    core.mode === 'anonymous' ||
    (core.mode === 'canceled' && surface !== 'account_billing');
  const showBindCta = core.mode === 'paid_unbound' && Boolean(context.leagueId);
  const showManageBilling =
    core.mode === 'subscribed_bound' || core.mode === 'past_due' || core.mode === 'canceled';
  const showStatusCard =
    core.mode === 'subscribed_bound' ||
    core.mode === 'comped' ||
    core.mode === 'past_due' ||
    core.mode === 'canceled';
  const showReturnToSetup = Boolean(context.leagueId) && core.mode === 'subscribed_bound';

  let disabledBanner: LscBillingPresentation['disabledBanner'] = 'none';
  let disabledReason: string | undefined;
  if (core.mode === 'ui_disabled') {
    disabledBanner = 'ui_disabled';
    disabledReason = LSC_BILLING_UI_DISABLED_MESSAGE;
  } else if (
    core.mode === 'checkout_disabled' ||
    core.mode === 'unknown' ||
    (core.mode === 'past_due' && !showCheckoutActions)
  ) {
    disabledBanner = 'lifecycle';
    disabledReason =
      core.lifecycleMessage ||
      'Checkout is temporarily unavailable. Try again later or contact support.';
  }

  return {
    mode: core.mode,
    headline: buildHeadline(core, surface),
    valueProposition: surfaceValueProposition(surface),
    detailLines: buildDetailLines(core, surface),
    planNotes: LSC_PRICING_PLAN_NOTES,
    primaryCta: ctas.primary,
    secondaryCtas: ctas.secondary,
    showCheckoutActions,
    showPricingCards,
    showBindCta,
    showManageBilling,
    showStatusCard,
    showReturnToSetup,
    disabledBanner,
    disabledReason,
    reasonCodes: core.reasonCodes,
  };
}

/** Build presentation input helpers from raw billing status (league surfaces). */
export function buildLeaguePresentationInput(
  status: LscBillingStatusResponse | null,
  surface: Extract<BillingPresentationSurface, 'setup_subscription' | 'league_billing_settings'>,
  viewer: { isOwner: boolean; isOperator: boolean },
  context: { billingUiEnabled: boolean; leagueId: string; leagueArchived?: boolean }
): ResolveLscBillingPresentationInput {
  return {
    status,
    surface,
    viewer: {
      isAuthenticated: true,
      isOwner: viewer.isOwner,
      isOperator: viewer.isOperator,
    },
    context,
  };
}

/** @deprecated Prefer resolveLscBillingPresentation — kept for league checkout tests. */
export function canShowLeagueCheckoutFromPresentation(
  presentation: LscBillingPresentation
): boolean {
  return presentation.showCheckoutActions;
}

/** @deprecated Prefer resolveLscBillingPresentation — kept for league checkout tests. */
export function shouldShowLeagueCheckoutUnavailableFromPresentation(
  presentation: LscBillingPresentation
): boolean {
  return (
    presentation.disabledBanner === 'lifecycle' &&
    !presentation.showCheckoutActions &&
    !presentation.showBindCta &&
    !presentation.showStatusCard &&
    presentation.mode !== 'owner_managed' &&
    presentation.mode !== 'comped'
  );
}

export { LSC_SUBSCRIPTION_VALUE_THEMES };
